import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { trackEvent, trackConversion } from '../../hooks/useGoogleAnalytics';
import { useToast } from '../../components/ui/Toast';
import { friendlyError } from '../../utils/errorMessages';
import { supabase } from '../../lib/supabase';

const Login = () => {
  // Detect OAuth callback to avoid flashing login form
  const isOAuthCallback = () => {
    const hash = window.location?.hash;
    const hashParams = new URLSearchParams(hash?.substring(1) || '');
    const searchParams = new URLSearchParams(window.location?.search);
    return !!(hashParams?.get('access_token') || searchParams?.get('code'));
  };

  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(isOAuthCallback());
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const { user, signIn, signUp, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  // Comprehensive OAuth callback handler with multiple fallback methods
  useEffect(() => {
    const handleOAuthCallback = async () => {
      // Method 1: Check for hash fragment (current Supabase default)
      const hash = window.location?.hash;
      const hashParams = new URLSearchParams(hash.substring(1));
      const accessToken = hashParams?.get('access_token');
      const refreshToken = hashParams?.get('refresh_token');
      const errorParam = hashParams?.get('error');
      const errorDescription = hashParams?.get('error_description');

      // Handle OAuth errors
      if (errorParam) {
        setError(`Authentication failed: ${errorDescription || errorParam}`);
        window.history?.replaceState(null, '', window.location?.pathname);
        return;
      }

      // If we have tokens in the hash
      if (accessToken && refreshToken) {
        setLoading(true);

        try {
          // Method 1a: Let Supabase auto-process (wait for it)
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          let { data: { session }, error: sessionError } = await supabase?.auth?.getSession();

          // Method 1b: If auto-processing failed, manually set session
          if (!session && !sessionError) {
            const { data, error: setSessionError } = await supabase?.auth?.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });
            
            if (setSessionError) {
              throw setSessionError;
            }
            session = data?.session;
          }

          // Method 1c: Poll for session establishment (fallback)
          if (!session) {
            let attempts = 0;
            const maxAttempts = 10;
            
            while (attempts < maxAttempts && !session) {
              await new Promise(resolve => setTimeout(resolve, 500));
              const { data: { session: polledSession } } = await supabase?.auth?.getSession();
              session = polledSession;
              attempts++;
            }
          }

          if (session) {
            // Clean up URL and navigate directly to dashboard
            // This avoids the brief login form flash while AuthContext catches up
            window.history?.replaceState(null, '', window.location?.pathname);
            navigate('/dashboard', { replace: true });
            return;
          } else {
            throw new Error('Failed to establish session after all attempts');
          }
        } catch (err) {
          console.error('OAuth callback error:', err?.message);
          setError('Authentication failed. Please try again.');
          setLoading(false);
          window.history?.replaceState(null, '', window.location?.pathname);
        }
      }

      // Method 2: Check for query parameters (alternative OAuth flow)
      const searchParams = new URLSearchParams(window.location.search);
      const code = searchParams?.get('code');
      
      if (code) {
        setLoading(true);
        
        try {
          const { data, error: exchangeError } = await supabase?.auth?.exchangeCodeForSession(code);

          if (exchangeError) throw exchangeError;

          window.history?.replaceState(null, '', window.location?.pathname);
          navigate('/dashboard', { replace: true });
          return;
        } catch (err) {
          console.error('Code exchange error:', err?.message);
          setError('Authentication failed. Please try again.');
          setLoading(false);
        }
      }
    };

    handleOAuthCallback();
  }, []);

  useEffect(() => {
    if (user && !loading) {
      trackConversion('user_login');
      trackEvent('funnel_step', {
        step: 'login_complete',
        funnel: 'signup_flow',
        page_path: window.location?.pathname
      });
      navigate('/dashboard', { replace: true });
    }
  }, [user, loading, navigate]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }
    
    if (isSignUp && !fullName) {
      setError('Please enter your full name');
      return;
    }
    
    setLoading(true);
    setError('');
    setMessage('');

    try {
      if (isSignUp) {
        trackEvent('signup_attempt', {
          method: 'email',
          page_path: window.location?.pathname
        });
        const { error } = await signUp(email, password, fullName);
        if (error) throw error;
        
        trackConversion('user_signup');
        trackEvent('funnel_step', {
          step: 'signup_complete',
          funnel: 'signup_flow',
          page_path: window.location?.pathname
        });
        setMessage('Account created successfully! You can now sign in.');
        setIsSignUp(false);
        setPassword('');
        setFullName('');
      } else {
        trackEvent('login_attempt', {
          method: 'email',
          page_path: window.location?.pathname
        });
        const { data, error } = await signIn(email, password);
        if (error) throw error;
        
        // Conversion tracking happens in useEffect when user is set
      }
    } catch (err) {
      console.error('Authentication error:', err?.message);
      const msg = friendlyError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    try {
      const { error } = await signInWithGoogle();
      if (error) throw error;
    } catch (err) {
      console.error('Google sign in error:', err?.message);
      const msg = friendlyError(err);
      setError(msg);
      toast.error(msg);
      setLoading(false);
    }
  };

  // Show loading screen during OAuth callback processing
  if (loading && !error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted to-background flex items-center justify-center px-4 py-12">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Signing you in...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted to-background flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6">
            <Icon name="ArrowLeft" size={20} />
            <span className="text-sm">Back to Home</span>
          </Link>
          
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-accent mb-4">
            <Icon name="Code2" size={32} color="#FFFFFF" />
          </div>
          
          <h1 className="text-3xl font-bold text-foreground mb-2">
            {isSignUp ? 'Create Account' : 'Welcome Back'}
          </h1>
          <p className="text-muted-foreground">
            {isSignUp ? 'Start sharing your technical insights' : 'Sign in to continue publishing'}
          </p>
        </div>

        <div className="bg-card rounded-2xl border border-border shadow-lg p-8">
          {message && (
            <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg flex items-start gap-3">
              <Icon name="CheckCircle" size={20} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
              <p className="text-sm text-success flex-1">{message}</p>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
              <Icon name="AlertCircle" size={20} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
              <p className="text-sm text-destructive flex-1">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {isSignUp && (
              <Input
                label="Full Name"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e?.target?.value)}
                placeholder="John Doe"
                required={isSignUp}
              />
            )}

            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e?.target?.value)}
              placeholder="you@example.com"
              required
            />

            <Input
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e?.target?.value)}
              placeholder="••••••••"
              required
            />

            <Button
              type="submit"
              fullWidth
              size="lg"
              loading={loading}
              disabled={loading}
            >
              {isSignUp ? 'Create Account' : 'Sign In'}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
            </div>
          </div>

          <Button
            variant="outline"
            fullWidth
            size="lg"
            onClick={handleGoogleSignIn}
            disabled={loading}
            iconName="Chrome"
          >
            Google
          </Button>

          <div className="mt-6 text-center space-y-3">
            <button
              type="button"
              onClick={() => {
                setIsSignUp(!isSignUp);
                setError('');
                setMessage('');
              }}
              className="text-sm text-primary hover:underline"
            >
              {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
            </button>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;